// mouthSystem.ts: Basic articulatory mouth model to shape voice via formants
// Minimal, working mouth with key articulators for exploration
// Now includes autonomous learning and exploration

export interface MouthState {
    jawOpen: number;           // 0-1
    tongueFrontness: number;   // 0 (back) - 1 (front)
    tongueHeight: number;      // 0 (low) - 1 (high)
    lipRoundness: number;      // 0-1
    nasalPort: number;         // 0-1 (0 closed, 1 fully open)
}

export interface LearnedConfiguration {
    state: MouthState;
    usageCount: number;
    successRating: number;      // 0-1, based on network response/outcome
    lastUsed: number;
    formants: { f1: number; f2: number; f3: number };
}

export interface MouthExplorationState {
    curiosity: number;              // 0-1, drives exploration
    explorationPhase: 'resting' | 'exploring' | 'processing';
    phaseStartTime: number;
    lastExplorationTime: number;
    totalPhonations: number;
    successfulPhonations: number;
    learnedConfigs: Map<string, LearnedConfiguration>;
    currentExperiment?: MouthState;
}

export interface ProceduralMouthInputs {
    growthProgress: number;        // 0-1 progression from early to mature creature growth
    energyLevel: number;           // 0-1 normalized energy level
    linguisticComplexity: number;  // 0-1 normalized communication maturity
    step: number;
    growthVarianceSeed: number;
}

export class MouthSystem {
    private state: MouthState;
    private exploration: MouthExplorationState;

    // Exploration parameters
    private readonly CURIOSITY_GROWTH_RATE = 0.0005;
    private readonly DISCOVERY_BOOST = 0.15;
    private readonly EXPLORATION_COOLDOWN = 3000; // ms between explorations
    private readonly PHASE_REST_DURATION = 2000; // ms
    private readonly PHASE_EXPLORE_DURATION = 1000; // ms
    
    constructor() {
        this.state = {
            jawOpen: 0.4,
            tongueFrontness: 0.5,
            tongueHeight: 0.5,
            lipRoundness: 0.2,
            nasalPort: 0.0
        };
        
        this.exploration = {
            curiosity: 0.2, // Start with some curiosity
            explorationPhase: 'resting',
            phaseStartTime: Date.now(),
            lastExplorationTime: 0,
            totalPhonations: 0,
            successfulPhonations: 0,
            learnedConfigs: new Map()
        };
    }

    getState(): MouthState {
        return { ...this.state };
    }

    setState(partial: Partial<MouthState>): void {
        this.state = { ...this.state, ...partial };
        this.clampState();
    }

    // Procedurally develop articulators from simulation growth signals.
    // This keeps mouth behavior coupled to each simulation's evolution rather than manual UI input.
    updateProceduralState(inputs: ProceduralMouthInputs): void {
        const growth = this.clamp01(inputs.growthProgress);
        const energy = this.clamp01(inputs.energyLevel);
        const complexity = this.clamp01(inputs.linguisticComplexity);

        const t = inputs.step * 0.012 + inputs.growthVarianceSeed * 12.7;
        const pulseA = Math.sin(t);
        const pulseB = Math.cos(t * 0.73 + inputs.growthVarianceSeed * 5.1);

        const target: MouthState = {
            jawOpen: this.clamp01(0.18 + 0.5 * growth + 0.16 * (1 - energy) + 0.06 * pulseA),
            tongueFrontness: this.clamp01(0.3 + 0.45 * complexity + 0.1 * pulseB),
            tongueHeight: this.clamp01(0.7 - 0.45 * growth + 0.08 * Math.cos(t * 1.13)),
            lipRoundness: this.clamp01(0.1 + 0.38 * growth * (1 - complexity) + 0.08 * Math.sin(t * 0.57)),
            nasalPort: this.clamp01(0.03 + 0.22 * (1 - energy) + 0.2 * (1 - growth) + 0.04 * Math.cos(t * 0.41))
        };

        // Smoothly approach target so articulators evolve organically.
        const blend = 0.12;
        this.state.jawOpen += (target.jawOpen - this.state.jawOpen) * blend;
        this.state.tongueFrontness += (target.tongueFrontness - this.state.tongueFrontness) * blend;
        this.state.tongueHeight += (target.tongueHeight - this.state.tongueHeight) * blend;
        this.state.lipRoundness += (target.lipRoundness - this.state.lipRoundness) * blend;
        this.state.nasalPort += (target.nasalPort - this.state.nasalPort) * blend;
        this.clampState();
    }

    // Map articulators to approximate vowel-like formants (F1, F2, F3)
    // Simple, continuous mapping for interactive exploration
    computeFormants(): { f1: number; f2: number; f3: number } {
        const s = this.state;
        // F1 rises with jawOpen and tongue lowered
        const f1 = 300
            + 700 * s.jawOpen
            + 400 * (1 - s.tongueHeight);

        // F2 rises with tongue frontness, falls with lip rounding
        const f2 = 700
            + 1400 * s.tongueFrontness
            - 600 * s.lipRoundness;

        // F3 weakly reduced by lip rounding; add slight frontness effect
        const f3 = 2500
            - 400 * s.lipRoundness
            + 150 * (s.tongueFrontness - 0.5);

        return {
            f1: Math.max(200, Math.min(1200, f1)),
            f2: Math.max(600, Math.min(2800, f2)),
            f3: Math.max(1500, Math.min(3500, f3))
        };
    }

    // Breathy factor from nasal coupling and jaw aperture (rough heuristic)
    computeBreathiness(): number {
        const s = this.state;
        const breathiness = 0.15 + 0.5 * s.nasalPort + 0.2 * s.jawOpen;
        return Math.max(0, Math.min(1, breathiness));
    }

    private clampState(): void {
        this.state.jawOpen = this.clamp01(this.state.jawOpen);
        this.state.tongueFrontness = this.clamp01(this.state.tongueFrontness);
        this.state.tongueHeight = this.clamp01(this.state.tongueHeight);
        this.state.lipRoundness = this.clamp01(this.state.lipRoundness);
        this.state.nasalPort = this.clamp01(this.state.nasalPort);
    }

    private clamp01(v: number): number {
        return Math.max(0, Math.min(1, v));
    }

    // === AUTONOMOUS EXPLORATION AND LEARNING ===

    getExplorationState(): MouthExplorationState {
        return { ...this.exploration };
    }

    // Update exploration state - called each simulation step
    updateExploration(dt: number, networkEnergy: number, linguisticComplexity: number): void {
        const now = Date.now();
        const phaseElapsed = now - this.exploration.phaseStartTime;

        // Grow curiosity based on linguistic development and time
        const baseGrowth = this.CURIOSITY_GROWTH_RATE * (1 + linguisticComplexity * 0.1);
        this.exploration.curiosity = Math.min(1, this.exploration.curiosity + baseGrowth);

        // Update exploration phase
        switch (this.exploration.explorationPhase) {
            case 'resting':
                if (phaseElapsed > this.PHASE_REST_DURATION && 
                    this.exploration.curiosity > 0.4 &&
                    now - this.exploration.lastExplorationTime > this.EXPLORATION_COOLDOWN) {
                    this.exploration.explorationPhase = 'exploring';
                    this.exploration.phaseStartTime = now;
                }
                break;

            case 'exploring':
                if (phaseElapsed > this.PHASE_EXPLORE_DURATION) {
                    this.exploration.explorationPhase = 'processing';
                    this.exploration.phaseStartTime = now;
                }
                break;

            case 'processing':
                // Brief processing phase, then return to resting
                this.exploration.explorationPhase = 'resting';
                this.exploration.phaseStartTime = now;
                break;
        }
    }

    // Generate next exploration target - tries new or revisits learned configs
    generateExplorationTarget(): MouthState | null {
        if (this.exploration.explorationPhase !== 'exploring') return null;

        // Sometimes use a learned good configuration (exploitation)
        if (this.exploration.learnedConfigs.size > 0 && Math.random() < 0.4) {
            const configs = Array.from(this.exploration.learnedConfigs.values());
            // Pick based on success rating and recency
            configs.sort((a, b) => {
                const aScore = a.successRating * 0.7 + (1 - (Date.now() - a.lastUsed) / 60000) * 0.3;
                const bScore = b.successRating * 0.7 + (1 - (Date.now() - b.lastUsed) / 60000) * 0.3;
                return bScore - aScore;
            });
            if (configs.length > 0 && configs[0].successRating > 0.5) {
                return { ...configs[0].state };
            }
        }

        // Exploration: try something new
        // Gradually explore space around current state or known good configs
        const exploreDistance = 0.2 + (1 - this.exploration.curiosity) * 0.3; // More random when less curious
        
        let baseState: MouthState;
        if (this.exploration.learnedConfigs.size > 0 && Math.random() < 0.6) {
            // Explore near a learned config
            const configs = Array.from(this.exploration.learnedConfigs.values());
            const randomConfig = configs[Math.floor(Math.random() * configs.length)];
            baseState = { ...randomConfig.state };
        } else {
            // Explore near current state
            baseState = { ...this.state };
        }

        // Add random variation
        const variation = () => (Math.random() - 0.5) * exploreDistance;
        return {
            jawOpen: Math.max(0, Math.min(1, baseState.jawOpen + variation())),
            tongueFrontness: Math.max(0, Math.min(1, baseState.tongueFrontness + variation())),
            tongueHeight: Math.max(0, Math.min(1, baseState.tongueHeight + variation())),
            lipRoundness: Math.max(0, Math.min(1, baseState.lipRoundness + variation())),
            nasalPort: Math.max(0, Math.min(1, baseState.nasalPort + variation() * 0.5)) // Less variation in nasal
        };
    }

    // Learn from a phonation outcome
    learnFromPhonation(state: MouthState, success: boolean, networkResponse: number): void {
        const key = this.configKey(state);
        const rating = success ? 0.5 + networkResponse * 0.5 : 0.2;

        if (this.exploration.learnedConfigs.has(key)) {
            const learned = this.exploration.learnedConfigs.get(key)!;
            // Update: weighted average of old rating and new outcome
            learned.successRating = learned.successRating * 0.7 + rating * 0.3;
            learned.usageCount++;
            learned.lastUsed = Date.now();
        } else {
            // New configuration
            this.exploration.learnedConfigs.set(key, {
                state: { ...state },
                usageCount: 1,
                successRating: rating,
                lastUsed: Date.now(),
                formants: this.computeFormantsForState(state)
            });
        }

        if (success) {
            this.exploration.successfulPhonations++;
            this.exploration.curiosity = Math.min(1, this.exploration.curiosity + this.DISCOVERY_BOOST);
        }
        this.exploration.totalPhonations++;
    }

    // Try autonomous phonation if conditions allow
    attemptAutonomousPhonation(
        canPhonate: () => boolean,
        doPhonate: (state: MouthState) => void,
        getNetworkResponse: () => number
    ): boolean {
        if (this.exploration.explorationPhase !== 'exploring') return false;
        if (!canPhonate()) return false;

        const target = this.generateExplorationTarget();
        if (!target) return false;

        // Save experiment
        this.exploration.currentExperiment = target;
        this.setState(target);

        // Attempt phonation
        doPhonate(target);

        // Evaluate outcome (async, will be called back)
        const networkResponse = getNetworkResponse();
        const success = networkResponse > 0.3; // Threshold for "interesting" response

        this.learnFromPhonation(target, success, networkResponse);
        this.exploration.lastExplorationTime = Date.now();

        return true;
    }

    // Helper: generate unique key for a mouth configuration
    private configKey(state: MouthState): string {
        // Round to 2 decimals for grouping similar configs
        const round = (v: number) => Math.round(v * 100) / 100;
        return `${round(state.jawOpen)},${round(state.tongueFrontness)},${round(state.tongueHeight)},${round(state.lipRoundness)},${round(state.nasalPort)}`;
    }

    // Compute formants for a given state (used when learning)
    private computeFormantsForState(s: MouthState): { f1: number; f2: number; f3: number } {
        const f1 = 300 + 700 * s.jawOpen + 400 * (1 - s.tongueHeight);
        const f2 = 700 + 1400 * s.tongueFrontness - 600 * s.lipRoundness;
        const f3 = 2500 - 400 * s.lipRoundness + 150 * (s.tongueFrontness - 0.5);
        return {
            f1: Math.max(200, Math.min(1200, f1)),
            f2: Math.max(600, Math.min(2800, f2)),
            f3: Math.max(1500, Math.min(3500, f3))
        };
    }
}


